import React, { useEffect, useRef, useState } from "react";

import { classNames } from "@calcom/lib";
import { Button, Label, Switch, TextField } from "@calcom/ui";

import { formatAmountToTwoDecimals, formatNumber } from "@lib/practice/helper";

import { DollarIcon } from "@components/Icons";
import { LiteralAvatar } from "@components/LiteralAvatar";

export type FamilyMember = {
  patientId: number;
  name: string;
  type: string;
  balance: number;
};

export type Allocation = {
  patientId: number;
  amount: number;
};

type Props = {
  members: FamilyMember[];
  totalAmount: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  allocations: Allocation[];
  onAllocationsChange: (allocations: Allocation[]) => void;
};

/** Split `total` across `count` parts in cents so the sum always equals the total. */
export function splitAmountEvenly(total: number, count: number): number[] {
  if (count <= 0) return [];
  const totalCents = Math.round(total * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents % count;
  return Array.from({ length: count }, (_, i) => (base + (i < remainder ? 1 : 0)) / 100);
}

function buildEvenAllocations(memberIds: number[], total: number): Allocation[] {
  const amounts = splitAmountEvenly(total, memberIds.length);
  return memberIds.map((patientId, i) => ({
    patientId,
    amount: amounts[i] ?? 0,
  }));
}

export default function FamilyPaymentAllocation({
  members,
  totalAmount,
  enabled,
  onEnabledChange,
  allocations,
  onAllocationsChange,
}: Props) {
  const [selected, setSelected] = useState<Set<number>>(() => new Set(members.map((m) => m.patientId)));
  const manualOverrideRef = useRef(false);
  const memberKey = members.map((m) => m.patientId).join(",");
  const selectedKey = [...selected].sort((a, b) => a - b).join(",");

  const total = parseFloat(totalAmount) || 0;
  const allocatedCents = allocations.reduce((sum, a) => sum + Math.round(a.amount * 100), 0);
  const totalCents = Math.round(total * 100);
  const allocatedSum = allocatedCents / 100;
  const isBalanced = !enabled || allocatedCents === totalCents;
  const difference = (totalCents - allocatedCents) / 100;

  // Reset selection when family membership changes
  useEffect(() => {
    const ids = new Set(members.map((m) => m.patientId));
    setSelected(ids);
    manualOverrideRef.current = false;
    if (enabled && members.length > 1) {
      onAllocationsChange(buildEvenAllocations([...ids], parseFloat(totalAmount) || 0));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberKey]);

  const prevTotalRef = useRef(totalAmount);

  // Re-even-split when total or selection changes (unless amounts were manually edited)
  useEffect(() => {
    if (!enabled || selected.size === 0) return;
    const totalChanged = prevTotalRef.current !== totalAmount;
    prevTotalRef.current = totalAmount;
    if (totalChanged) manualOverrideRef.current = false;
    if (manualOverrideRef.current) return;
    onAllocationsChange(buildEvenAllocations([...selected], total));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, totalAmount, selectedKey]);

  if (members.length <= 1) return null;

  const getAmount = (patientId: number) => {
    const found = allocations.find((a) => a.patientId === patientId);
    return found ? formatAmountToTwoDecimals(found.amount) : "0.00";
  };

  const toggleMember = (patientId: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(patientId)) {
        if (next.size === 1) return prev;
        next.delete(patientId);
      } else {
        next.add(patientId);
      }
      manualOverrideRef.current = false;
      return next;
    });
  };

  const handleAmountChange = (patientId: number, value: string) => {
    if (!selected.has(patientId)) return;
    manualOverrideRef.current = true;
    const amount = parseFloat(value);
    onAllocationsChange(
      [...selected].map((id) => ({
        patientId: id,
        amount:
          id === patientId
            ? isNaN(amount)
              ? 0
              : amount
            : allocations.find((a) => a.patientId === id)?.amount ?? 0,
      }))
    );
  };

  const handleAmountBlur = (patientId: number, value: string) => {
    handleAmountChange(patientId, formatAmountToTwoDecimals(value));
  };

  const resetEvenSplit = () => {
    manualOverrideRef.current = false;
    onAllocationsChange(buildEvenAllocations([...selected], total));
  };

  return (
    <div id="terminal-payment-family-allocation" className="space-y-3">
      <Switch
        label="Split payment across family members"
        checked={enabled}
        onCheckedChange={(checked) => {
          onEnabledChange(checked);
          if (checked) {
            manualOverrideRef.current = false;
            const ids = new Set(members.map((m) => m.patientId));
            setSelected(ids);
            onAllocationsChange(buildEvenAllocations([...ids], total));
          } else {
            onAllocationsChange([]);
          }
        }}
      />

      {enabled && (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-[#F9FAFA]">
          <div className="flex items-center justify-between gap-2 border-b border-gray-200 px-3 py-2.5">
            <div>
              <p className="text-sm font-medium text-gray-900">Family allocation</p>
              <p className="text-xs text-gray-500">
                Total ${formatNumber(total, "balance")} · tap members to include · edit amounts to override
              </p>
            </div>
            <Button type="button" color="secondary" className="shrink-0 text-xs" onClick={resetEvenSplit}>
              Even split
            </Button>
          </div>

          <ul className="divide-y divide-gray-200 bg-white">
            {members.map((member) => {
              const isSelected = selected.has(member.patientId);
              return (
                <li
                  key={member.patientId}
                  className={classNames(
                    "flex cursor-pointer items-center gap-3 px-3 py-3 transition-colors",
                    isSelected ? "bg-[#E4FFF5]" : "bg-white hover:bg-gray-50"
                  )}
                  onClick={() => toggleMember(member.patientId)}>
                  <div
                    className={classNames(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs",
                      isSelected
                        ? "border-gray-900 bg-gray-900 text-white"
                        : "border-gray-300 bg-white text-transparent"
                    )}
                    aria-hidden>
                    ✓
                  </div>
                  <LiteralAvatar fullName={member.name} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900">{member.name}</p>
                    <p className="truncate text-xs text-gray-500">
                      {member.type} · Balance ${formatNumber(member.balance, "balance")}
                    </p>
                  </div>
                  <div className="w-28 shrink-0" onClick={(e) => e.stopPropagation()}>
                    <Label className="sr-only">Amount for {member.name}</Label>
                    <TextField
                      className="rounded-lg text-gray-900"
                      label=""
                      addOnLeading={<DollarIcon />}
                      disabled={!isSelected}
                      value={isSelected ? getAmount(member.patientId) : "0.00"}
                      onChange={(e) => handleAmountChange(member.patientId, e.target.value)}
                      onBlur={(e) => handleAmountBlur(member.patientId, e.target.value)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>

          <div
            className={classNames(
              "flex items-center justify-between border-t border-gray-200 px-3 py-2.5 text-sm",
              isBalanced ? "text-gray-600" : "bg-red-50 text-red-700"
            )}>
            <span>Allocated</span>
            <span className="font-medium">
              ${formatNumber(allocatedSum, "balance")} / ${formatNumber(total, "balance")}
              {!isBalanced && (
                <span className="ml-2 text-xs">
                  (
                  {difference > 0
                    ? `$${formatNumber(difference, "balance")} left`
                    : `$${formatNumber(Math.abs(difference), "balance")} over`}
                  )
                </span>
              )}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
